(() => {
  'use strict';

  const focusableSelector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const setBackdrop = (visible) => {
    const backdrop = document.querySelector('[data-site-backdrop]');
    if (!backdrop) return;
    backdrop.hidden = !visible;
    document.body.classList.toggle('has-overlay', visible);
  };

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(focusableSelector)].filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initHeader = (header) => {
    if (!header || header.dataset.initialized) return;
    header.dataset.initialized = 'true';

    const drawer = header.querySelector('[data-mobile-menu]');
    const openButton = header.querySelector('[data-mobile-menu-open]');
    const closeButton = header.querySelector('[data-mobile-menu-close]');
    const desktopViewport = window.matchMedia('(min-width: 990px)');
    let lastFocused = null;

    const closeDropdowns = (except) => {
      header.querySelectorAll('[data-nav-toggle][aria-expanded="true"]').forEach((toggle) => {
        if (toggle === except) return;
        toggle.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
      if (!except) setBackdrop(false);
    };

    const setDropdown = (toggle, open) => {
      if (!toggle) return;
      if (open) closeDropdowns(toggle);
      toggle.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
      if (open) {
        setBackdrop(true);
      } else if (!header.querySelector('[data-nav-toggle][aria-expanded="true"]')) {
        setBackdrop(false);
      }
    };

    const setDrawer = (open) => {
      if (!drawer || !openButton) return;
      const wasOpen = drawer.classList.contains('is-open');
      openButton.setAttribute('aria-expanded', String(open));
      drawer.setAttribute('aria-hidden', String(!open));
      drawer.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
      if (open) setBackdrop(true);
      if (open) {
        if (!wasOpen) lastFocused = document.activeElement;
        (closeButton || drawer.querySelector(focusableSelector))?.focus();
      } else if (wasOpen) {
        lastFocused?.focus();
        lastFocused = null;
        if (!header.querySelector('[data-nav-toggle][aria-expanded="true"]')) setBackdrop(false);
      }
    };

    openButton?.addEventListener('click', () => setDrawer(true));
    closeButton?.addEventListener('click', () => setDrawer(false));
    document.querySelector('[data-site-backdrop]')?.addEventListener('click', () => {
      setDrawer(false);
      closeDropdowns();
    });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setDrawer(false);
      }
      trapFocus(drawer, event);
    });

    header.querySelectorAll('[data-nav-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';
        setDropdown(toggle, shouldOpen);
      });

      const catalogItem = toggle.closest('.desktop-nav__item--catalog');
      if (!catalogItem) return;
      let openTimer;
      let closeTimer;

      catalogItem.addEventListener('pointerenter', (event) => {
        if (!desktopViewport.matches || event.pointerType !== 'mouse') return;
        window.clearTimeout(closeTimer);
        if (toggle.getAttribute('aria-expanded') === 'true') return;
        openTimer = window.setTimeout(() => setDropdown(toggle, true), 120);
      });
      catalogItem.addEventListener('pointerleave', (event) => {
        if (!desktopViewport.matches || event.pointerType !== 'mouse') return;
        window.clearTimeout(openTimer);
        closeTimer = window.setTimeout(() => {
          if (!catalogItem.contains(document.activeElement)) setDropdown(toggle, false);
        }, 140);
      });
      catalogItem.addEventListener('focusout', (event) => {
        if (desktopViewport.matches && !catalogItem.contains(event.relatedTarget)) setDropdown(toggle, false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (drawer?.classList.contains('is-open')) {
          event.preventDefault();
          setDrawer(false);
          return;
        }
        const expandedToggle = header.querySelector('[data-nav-toggle][aria-expanded="true"]');
        if (expandedToggle) {
          event.preventDefault();
          closeDropdowns();
          expandedToggle.focus();
        }
      }
    });

    let stickyThreshold = 20;

    const visibleMinHeight = (element, fallback) => {
      if (!element) return 0;
      const styles = window.getComputedStyle(element);
      return styles.display === 'none' ? 0 : Number.parseFloat(styles.minHeight) || fallback;
    };

    const updateHeaderMetrics = () => {
      const announcementBar = document.querySelector('.announcement-bar');
      const announcementHeight = announcementBar?.offsetHeight || 0;
      const utilityHeight = visibleMinHeight(header.querySelector('.header-utility'), 36);
      const brandingHeight = visibleMinHeight(header.querySelector('.header-main'), 94);
      stickyThreshold = desktopViewport.matches ? utilityHeight + brandingHeight : 20;
      header.style.setProperty('--sticky-announcement-height', `${announcementHeight}px`);
    };

    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > stickyThreshold);
    updateHeaderMetrics();
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
    window.addEventListener('resize', () => {
      updateHeaderMetrics();
      updateHeader();
    }, { passive: true });

    const searchDialog = header.querySelector('[data-search-dialog]');
    header.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!searchDialog) return;
        searchDialog.showModal();
        searchDialog.querySelector('input[type="search"]')?.focus();
      });
    });
    header.querySelector('[data-search-close]')?.addEventListener('click', () => searchDialog?.close());
    searchDialog?.addEventListener('click', (event) => {
      if (event.target === searchDialog) searchDialog.close();
    });
  };

  const initCarousel = (carousel) => {
    if (carousel.dataset.initialized) return;
    carousel.dataset.initialized = 'true';
    const scroller = carousel.querySelector('[data-carousel-track]');
    if (!scroller) return;
    const move = (direction) => {
      const card = scroller.firstElementChild;
      const distance = card ? card.getBoundingClientRect().width + 24 : scroller.clientWidth * 0.8;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scroller.scrollBy({ left: distance * direction, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    };
    carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => move(-1));
    carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => move(1));
  };

  const initQuantity = (root) => {
    root.querySelectorAll('[data-quantity]').forEach((wrapper) => {
      if (wrapper.dataset.initialized) return;
      wrapper.dataset.initialized = 'true';
      const input = wrapper.querySelector('input[type="number"]');
      wrapper.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!input) return;
          const step = Number(input.step) || 1;
          const min = Number(input.min) || 0;
          const next = Number(input.value) + Number(button.dataset.quantityChange) * step;
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  const initRoot = (root = document) => {
    root.querySelectorAll('[data-site-header]').forEach(initHeader);
    root.querySelectorAll('[data-carousel]').forEach(initCarousel);
    initQuantity(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initRoot();
    document.querySelector('[data-form-status]')?.focus();
    document.querySelectorAll('[data-back-to-top]').forEach((button) => {
      const updateVisibility = () => button.classList.toggle('is-visible', window.scrollY > 600);
      updateVisibility();
      window.addEventListener('scroll', updateVisibility, { passive: true });
      button.addEventListener('click', () => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    if (event.target.matches?.('.section-announcement') || event.target.querySelector?.('.announcement-bar')) {
      window.dispatchEvent(new Event('resize'));
    }
  });
})();
